import { useAuth0 } from '@auth0/auth0-react'
import { useTheme } from '@mui/material'
import { isEqual } from 'lodash'
import { useEffect, useEffectEvent, useRef } from 'react'
import { useLocation } from 'react-router'
import { useAppSelector } from 'store/configureStore'
import {
  loadQFMessenger,
  QF_MESSENGER_AUDIENCE,
  QF_MESSENGER_SCOPE,
  reportMessengerTokenError,
} from './qfMessengerRuntime'

const launcher: QFMessengerLauncherOptions = {
  horizontalOffset: 8,
  bottomOffset: 8,
  size: 48,
  iconSize: 28,
}

export const QFMessenger = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } =
    useAuth0()
  const theme = useTheme()
  const location = useLocation()
  const bootedUser = useRef<string | null>(null)
  const bootedThemeMode = useRef<'light' | 'dark' | null>(null)
  const partyId = useAppSelector((state) => state.user.organizationContext?.id)
  const projectId = useAppSelector((state) => state.project.activeProject?.id)
  const bomItemIds = useAppSelector(
    (state) => state.project.selectedBomItemPointers?.map((x) => x.id),
    isEqual,
  )
  const projectState = useAppSelector(
    (state) => state.project.activeProject?.status,
  )

  const context: QFMessengerContext = {
    partyId: partyId ?? null,
    projectId: projectId ?? null,
    bomItemId: bomItemIds ? JSON.stringify(bomItemIds || []) : null,
    workflowStage: projectState ?? null,
    route: location.pathname,
  }

  const boot = useEffectEvent(() => {
    window.QFMessenger?.('boot', {
      getToken: async () => {
        try {
          return await getAccessTokenSilently({
            authorizationParams: {
              audience: QF_MESSENGER_AUDIENCE,
              scope: QF_MESSENGER_SCOPE,
            },
          })
        } catch (error) {
          reportMessengerTokenError(error)
          return null
        }
      },
      context,
      launcher,
      theme: {
        mode: theme.palette.mode,
      },
    })
  })

  const updateContext = useEffectEvent(() => {
    window.QFMessenger?.('updateContext', context)
  })

  useEffect(() => {
    loadQFMessenger()
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      bootedUser.current = null
      bootedThemeMode.current = null
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user?.sub) return
    if (
      bootedUser.current === user.sub &&
      bootedThemeMode.current === theme.palette.mode
    ) {
      return
    }

    bootedUser.current = user.sub
    bootedThemeMode.current = theme.palette.mode
    boot()
  }, [isAuthenticated, isLoading, theme.palette.mode, user?.sub])

  useEffect(() => {
    if (!isAuthenticated || bootedUser.current !== user?.sub) return
    updateContext()
  }, [
    bomItemIds,
    isAuthenticated,
    location.pathname,
    partyId,
    projectId,
    user?.sub,
    projectState,
  ])

  return null
}
